import { ConfigProvider as AntdConfigProvider, type ThemeConfig, useGenerateTheme } from '@pba/react-components';
import { useGetTheme } from '@pba/react-theme';
import { useAntdLocale } from 'i18n/antd';
import { type ComponentProps, useMemo } from 'react';
import assetsColors from 'common/styles/colors.json';
import variables from 'common/styles/variables.json';

export type AntdThemeProperties = Omit<ComponentProps<typeof AntdConfigProvider>, 'locale'>;

const AntdTheme = (props: AntdThemeProperties) => {
  const locale = useAntdLocale();
  const { color, fontWeight } = useGetTheme();
  const reactComponentsTheme = useGenerateTheme();
  const theme = useMemo(
    (): ThemeConfig =>
      /* eslint-disable @typescript-eslint/naming-convention -- Antd interface */
      ({
        ...reactComponentsTheme,
        components: {
          ...reactComponentsTheme.components,
          Avatar: {
            containerSize: 40,
          },
          Badge: {
            fontSize: 12,
            statusSize: 12,
          },
          Form: {
            labelColor: assetsColors.TEXT_ICONS_MARGINAL,
            labelFontSize: 12,
            labelHeight: 20,
            verticalLabelPadding: '0 0 2px',
          },
          Layout: {
            headerBg: assetsColors.GENERAL_WHITE,
            siderBg: assetsColors.GENERAL_BG_DARK,
          },
          Progress: {
            remainingColor: assetsColors.GENERAL_BG_LIGHT,
          },
          Segmented: {
            fontSizeLG: 14,
          },
          Select: {
            optionActiveBg: `${variables.brandColor.value}1A`,
          },
          // TODO: Move the files table text, header, and icon tokens into the react-components theme.
          Table: {
            colorText: color.text.default,
            fontWeightStrong: fontWeight.base,
            headerBorderRadius: 0,
            headerColor: color.text.default,
          },
          Tabs: {
            colorBorderSecondary: 'transparent',
            horizontalItemGutter: 0,
            horizontalItemPadding: '8px 16px',
            horizontalMargin: '0',
            titleFontSize: 16,
          },
          Tag: {
            colorBorder: 'transparent',
            fontSize: 12,
          },
        },
        token: {
          ...reactComponentsTheme.token,
          borderRadius: variables.spaceXss.value,
          colorBgLayout: assetsColors.GENERAL_BG_LIGHT,
          colorBorder: assetsColors.ELEMENTS_BORDERS_DIVIDERS,
          colorBorderSecondary: assetsColors.ELEMENTS_BORDERS_DIVIDERS,
          colorIcon: color.text.placeholder,
          colorIconHover: color.text.secondary,
          fontSizeIcon: 11,
        },
      }),
    /* eslint-enable @typescript-eslint/naming-convention -- Antd interface */
    [color.text.default, color.text.placeholder, color.text.secondary, fontWeight.base, reactComponentsTheme],
  );

  return <AntdConfigProvider locale={locale} theme={theme} {...props} />;
};

export default AntdTheme;
